import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogIn, ChevronDown, LayoutDashboard, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { company, mainNav } from "@/content/site";
import { useAuth } from "@/features/auth/useAuth";
import { ROLE_HOME, describeUserRole, userInitials, type AppUser } from "@/features/auth/types";

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const dashboardHref = user ? ROLE_HOME[user.role] : "/app";
  const displayName = user ? user.name || user.email.split("@")[0] : "";
  const roleLabel = describeUserRole(user);

  // Once scrolled the floating bar tightens and gains a stronger glass/shadow so
  // it stays legible as content slides under it. Hysteresis (shrink past 80px,
  // grow back under 40px) avoids the wiggle a single threshold causes on short
  // pages where the height change nudges scrollY back over the line.
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled((prev) => (prev ? y > 40 : y > 80));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  async function handleSignOut() {
    setOpen(false);
    await signOut();
    navigate("/");
  }

  return (
    // Floating, translucent, rounded nav: padded off the top/sides so it hovers
    // over the page (content scrolls underneath in the gaps) instead of a
    // full-bleed bar pinned to the very edge.
    <header className="sticky top-0 z-40 px-3 pt-3 sm:px-4 sm:pt-4">
      <div
        className={cn(
          "mx-auto flex max-w-[1200px] items-center justify-between gap-3 rounded-2xl border border-white/50 bg-white/70 px-3 backdrop-blur-md transition-all duration-300 sm:px-4",
          scrolled ? "h-14 shadow-lg shadow-brand-900/10" : "h-16 shadow-md shadow-brand-900/5 sm:h-[4.5rem]",
        )}
      >
        <Link to="/" className="flex items-center gap-2 font-bold text-brand-900" aria-label={`${company.name} home`}>
          <img
            src="/brand/logo.png"
            alt={company.name}
            className={cn("w-auto transition-all duration-300", scrolled ? "h-9" : "h-10 sm:h-12")}
          />
          <span className="sr-only">{company.name}</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {mainNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "relative rounded-md px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-900/5",
                  isActive &&
                    "text-brand-900 after:absolute after:inset-x-3 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-accent-500",
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          {user ? (
            <AccountMenu
              user={user}
              displayName={displayName}
              roleLabel={roleLabel}
              dashboardHref={dashboardHref}
              onSignOut={handleSignOut}
            />
          ) : (
            <Link
              to="/app"
              className="flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold text-brand-700 hover:bg-brand-900/5"
            >
              <LogIn className="h-4 w-4" /> Sign in
            </Link>
          )}
          <Link to="/contact" className={cn(buttonVariants({ variant: "accent", size: "sm" }))}>
            Get a quote
          </Link>
        </div>

        <button
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-brand-900 hover:bg-brand-900/5 lg:hidden"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <nav className="mx-auto mt-2 max-w-[1200px] rounded-2xl border border-white/50 bg-white/90 p-2 shadow-lg shadow-brand-900/10 backdrop-blur-md lg:hidden">
          <div className="flex max-h-[calc(100dvh-6rem)] flex-col overflow-y-auto">
            {user && (
              <div className="mb-1 flex items-center gap-3 rounded-xl bg-brand-50 p-3">
                <Avatar name={displayName} className="h-10 w-10 text-sm" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-brand-900">{displayName}</div>
                  <div className="truncate text-xs text-brand-500">{user.email}</div>
                  <span className="mt-1 inline-block rounded-full bg-accent-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent-600">
                    {roleLabel}
                  </span>
                </div>
              </div>
            )}
            {mainNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-900/5"
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/track" onClick={() => setOpen(false)} className="rounded-md px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-900/5">
              Track a shipment
            </Link>
            {user ? (
              <>
                <Link
                  to={dashboardHref}
                  onClick={() => setOpen(false)}
                  className="mt-1 flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-brand-800 hover:bg-brand-900/5"
                >
                  <LayoutDashboard className="h-4 w-4 text-brand-500" /> Go to dashboard
                </Link>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-semibold text-brand-800 hover:bg-brand-900/5"
                >
                  <LogOut className="h-4 w-4 text-brand-500" /> Sign out
                </button>
              </>
            ) : (
              <Link to="/app" onClick={() => setOpen(false)} className="flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold text-accent-600">
                <LogIn className="h-4 w-4" /> Sign in
              </Link>
            )}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className={cn(buttonVariants({ variant: "accent" }), "mt-2 w-full justify-center sm:w-auto")}
            >
              Get a quote
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}

/** Gradient initials avatar used across the header account UI. */
function Avatar({ name, className }: { name: string; className?: string }) {
  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-800 to-accent-600 font-semibold text-white ring-2 ring-white",
        className,
      )}
      aria-hidden
    >
      {userInitials(name)}
    </span>
  );
}

/** Desktop signed-in chip that opens a small account dropdown (dashboard + sign out). */
function AccountMenu({
  user,
  displayName,
  roleLabel,
  dashboardHref,
  onSignOut,
}: {
  user: AppUser;
  displayName: string;
  roleLabel: string;
  dashboardHref: string;
  onSignOut: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Staff account names are often the role title itself (e.g. "HR / Recruiting
  // Manager"), so don't print the same string twice — fall back to a subtle label.
  const sameAsName = roleLabel && roleLabel.toLowerCase() === displayName.toLowerCase();
  const subLabel = sameAsName ? "Signed in" : roleLabel;

  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className={cn(
          "flex items-center gap-2 rounded-full border py-1 pl-1 pr-2 text-left transition-all",
          menuOpen
            ? "border-accent-300 bg-white shadow-md shadow-brand-900/10"
            : "border-brand-200/80 bg-white/60 hover:border-accent-300 hover:bg-white hover:shadow-sm",
        )}
      >
        <Avatar name={displayName} className="h-8 w-8 text-xs" />
        <span className="flex max-w-[10rem] flex-col leading-tight">
          <span className="truncate text-xs font-semibold text-brand-900">{displayName}</span>
          <span className="truncate text-[10px] font-medium uppercase tracking-wide text-brand-400">
            {subLabel}
          </span>
        </span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-brand-400 transition-transform", menuOpen && "rotate-180")} />
      </button>

      {menuOpen && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+0.5rem)] w-64 origin-top-right overflow-hidden rounded-2xl border border-brand-100 bg-white shadow-xl shadow-brand-900/10"
        >
          <div className="flex items-center gap-3 bg-brand-50 p-3">
            <Avatar name={displayName} className="h-11 w-11 text-sm" />
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-brand-900">{displayName}</div>
              <div className="truncate text-xs text-brand-500">{user.email}</div>
            </div>
          </div>
          <div className="border-b border-brand-100 px-3 pb-3 pt-2">
            <span className="inline-block rounded-full bg-accent-500/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent-600">
              {roleLabel}
            </span>
          </div>
          <div className="p-1.5">
            <Link
              to={dashboardHref}
              role="menuitem"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-brand-800 hover:bg-brand-900/5"
            >
              <LayoutDashboard className="h-4 w-4 text-brand-500" /> Go to dashboard
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={onSignOut}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-brand-800 hover:bg-red-50 hover:text-red-700"
            >
              <LogOut className="h-4 w-4 text-brand-500" /> Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
